import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";

import "./snackbar.css";

export function Snackbar({
	text,
	closeTime,
	onRemove,
}: {
	text: string;
	/** Use close time instead of duration so it also works on rerender */
	closeTime: number;
	onRemove: () => void;
}) {
	const VISIBLE_CLASS = "snackbar--visible";

	const snackbarRef = useRef<HTMLDivElement | null>(null);
	const timeoutId = useRef<number | null>(null);

	const [isVisible, setIsVisible] = useState<boolean>(false);

	const transitionendHandler = useCallback(() => {
		if (!snackbarRef.current) return;

		if (!snackbarRef.current.classList.contains(VISIBLE_CLASS)) {
			onRemove();
			snackbarRef.current?.removeEventListener("transitionend", transitionendHandler);
		}
	}, [onRemove]);

	const clickHandler = useCallback(() => {
		setIsVisible(false);
		snackbarRef.current?.addEventListener("transitionend", transitionendHandler);

		if (timeoutId.current) {
			window.clearTimeout(timeoutId.current);
			timeoutId.current = null;
		}
	}, [transitionendHandler]);

	useEffect(() => {
		const snackbarEl = snackbarRef.current;

		setIsVisible(true);

		const duration = closeTime - Date.now();
		if (duration <= 0) {
			setIsVisible(false);
			return () => undefined;
		}

		timeoutId.current = window.setTimeout(() => {
			setIsVisible(false);

			snackbarRef.current?.addEventListener("transitionend", transitionendHandler);
		}, duration);

		return () => {
			if (timeoutId.current) window.clearTimeout(timeoutId.current);
			snackbarEl?.removeEventListener("transitionend", transitionendHandler);
		};
	}, [closeTime, transitionendHandler]);

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
		<div
			ref={snackbarRef}
			className={classNames("snackbar", {
				[VISIBLE_CLASS]: isVisible,
			})}
			onClick={clickHandler}
			role="banner"
		>
			{text}
		</div>
	);
}
